import type { Region } from "../types.js";

// ---------- world data ----------
// REGION_DATA keeps its inferred literal-key type (via `satisfies`, not a
// `Record<string, Region>` annotation) so RegionKey below is the exact
// 8-key union, not `string` — every other region-keyed table
// (demography.ts, jurisdictions.ts, data/nobility.ts, placeNames.ts) then
// checks itself against RegionKey with its own `satisfies`, so forgetting a
// country in one of those tables is a compile error, not just a test
// failure (features.test.ts still covers the rest of each region's shape).
const REGION_DATA = {
  england: {
    name: { en: "England", ca: "Anglaterra" },
    places: [
      { en: "the village of Elmleigh, Kent", ca: "el poble d'Elmleigh, Kent" },
      { en: "the manor of Elton, Huntingdonshire", ca: "el senyoriu d'Elton, Huntingdonshire" },
      { en: "the vill of Netherstoke, Yorkshire", ca: "la vila de Netherstoke, Yorkshire" },
      { en: "the parish of Halesowen, Worcestershire", ca: "la parròquia de Halesowen, Worcestershire" },
      { en: "the fenland vill of Merewick, near Ely", ca: "la vila dels aiguamolls de Merewick, prop d'Ely" },
    ],
    maleNames: [
      "John",
      "William",
      "Robert",
      "Thomas",
      "Richard",
      "Henry",
      "Walter",
      "Geoffrey",
      "Adam",
      "Simon",
      "Nicholas",
      "Roger",
      "Hugh",
      "Gilbert",
      "Ralph",
      "Stephen",
      "Peter",
    ],
    femaleNames: [
      "Alice",
      "Agnes",
      "Joan",
      "Matilda",
      "Margery",
      "Isabella",
      "Emma",
      "Cecily",
      "Juliana",
      "Edith",
      "Beatrice",
      "Christina",
      "Avice",
      "Sibyl",
      "Amice",
    ],
    surnames: [
      "atte Wode",
      "Smith",
      "Carter",
      "Baker",
      "Webster",
      "Fletcher",
      "atte Well",
      "Shepherd",
      "Turner",
      "Clerk",
      "Reeve",
      "Fowler",
      "Chapman",
      "Underhill",
      "Frankeleyn",
      "Godwin",
    ],
    marriageF: [19, 24],
    marriageM: [23, 28],
    famine: [1315, 1317],
    famineName: { en: "the Great Famine", ca: "la Gran Fam" },
    warYears: [
      [1346, 1360],
      [1369, 1389],
      [1415, 1453],
      [1455, 1487],
    ],
    warNames: {
      1346: { en: "the king's wars in France", ca: "les guerres del rei a França" },
      1369: { en: "the French war", ca: "la guerra amb França" },
      1415: { en: "King Henry's war in France", ca: "la guerra del rei Enric a França" },
      1455: { en: "the wars between Lancaster and York", ca: "les guerres entre Lancaster i York" },
    },
    revolt: {
      year: 1381,
      name: { en: "the Great Rising of 1381", ca: "la Gran Revolta de 1381" },
      desc: {
        en: "when the commons rose against the poll tax, marched on London, and burned the Savoy Palace",
        ca: "quan el poble ras es va aixecar contra l'impost de capitació, va marxar sobre Londres i va cremar el palau de Savoy",
      },
    },
    pilgrim: [
      { en: "the shrine of St Thomas at Canterbury", ca: "el santuari de Sant Tomàs a Canterbury" },
      { en: "Our Lady of Walsingham", ca: "Nostra Senyora de Walsingham" },
      { en: "the shrine of St Cuthbert at Durham", ca: "el santuari de Sant Cuthbert a Durham" },
    ],
    currency: "shillings",
    landUnit: { en: "acres", ca: "acres" },
    inheritance: "impartible",
  },
  france: {
    name: { en: "France", ca: "França" },
    places: [
      { en: "the village of Saint-Ouen-le-Petit, in the Beauvaisis", ca: "el poble de Saint-Ouen-le-Petit, al Beauvaisis" },
      { en: "a parish in Normandy near Caen", ca: "una parròquia de Normandia, prop de Caen" },
      { en: "the village of Montcler, Languedoc", ca: "el poble de Montcler, Llenguadoc" },
      { en: "the vill of Fresnay, Burgundy", ca: "la vila de Fresnay, Borgonya" },
    ],
    maleNames: ["Jean", "Guillaume", "Pierre", "Jacques", "Colin", "Étienne", "Thomas", "Robert", "Michel", "Raymond", "Bernard", "Arnaud", "Perrin", "Denis"],
    femaleNames: ["Jeanne", "Marguerite", "Perrette", "Alison", "Isabelle", "Catherine", "Agnès", "Guillemette", "Jacquette", "Sibille", "Mahaut", "Denise"],
    surnames: [
      "le Charpentier",
      "du Bois",
      "Lefèvre",
      "Moreau",
      "de la Rue",
      "Boulanger",
      "Tisserand",
      "le Breton",
      "Fournier",
      "Pelletier",
      "Vachier",
      "Gastinel",
    ],
    marriageF: [17, 22],
    marriageM: [22, 27],
    famine: [1315, 1317],
    famineName: { en: "the Great Famine", ca: "la Gran Fam" },
    warYears: [
      [1337, 1360],
      [1369, 1389],
      [1415, 1453],
    ],
    warNames: {
      1337: { en: "the English war", ca: "la guerra anglesa" },
      1369: { en: "the war against the English", ca: "la guerra contra els anglesos" },
      1415: { en: "the English invasion and the wars of the mad king's reign", ca: "la invasió anglesa i les guerres del regnat del rei boig" },
    },
    revolt: {
      year: 1358,
      name: { en: "the Jacquerie of 1358", ca: "la Jacquerie de 1358" },
      desc: {
        en: "when the peasants of the Beauvaisis rose against the nobles amid the ruin of the English war",
        ca: "quan els pagesos del Beauvaisis es van aixecar contra els nobles enmig de la ruïna de la guerra anglesa",
      },
    },
    pilgrim: [
      { en: "Mont-Saint-Michel", ca: "Mont-Saint-Michel" },
      { en: "Saint-Martin of Tours", ca: "Sant Martí de Tours" },
      { en: "Notre-Dame du Puy", ca: "Notre-Dame du Puy" },
    ],
    currency: "sous",
    landUnit: { en: "arpents", ca: "arpents" },
    routiers: true,
    // Much of customary France (the pays de droit écrit south, and many
    // coutumes elsewhere) divided a holding among the sons rather than
    // passing it whole to one — unlike the strict primogeniture of
    // Catalonia's hereu or England's copyhold custom.
    inheritance: "partible",
  },
  catalonia: {
    name: { en: "the Crown of Aragon", ca: "la Corona d'Aragó" },
    places: [
      { en: "the village of Santa Coloma del Vallès", ca: "el poble de Santa Coloma del Vallès" },
      { en: "a mas-scattered parish in Osona", ca: "una parròquia de masos escampats a Osona" },
      { en: "the village of Vilamarí, near Girona", ca: "el poble de Vilamarí, prop de Girona" },
      { en: "the vill of Riudellots, in the Empordà", ca: "la vila de Riudellots, a l'Empordà" },
    ],
    maleNames: ["Pere", "Joan", "Bernat", "Guillem", "Ramon", "Berenguer", "Arnau", "Jaume", "Antoni", "Francesc", "Bartomeu", "Miquel", "Galceran"],
    femaleNames: ["Maria", "Elisenda", "Sança", "Blanca", "Constança", "Margarida", "Caterina", "Eulàlia", "Sibil·la", "Agnès", "Violant", "Francesca"],
    surnames: ["Ferrer", "Puig", "Riera", "Soler", "Vila", "Serra", "Mas", "Font", "Rovira", "Camps", "Oller", "Batlle", "Espígol"],
    marriageF: [16, 20],
    marriageM: [21, 26],
    famine: [1333, 1334],
    famineName: { en: "lo mal any primer, the first bad year", ca: "lo mal any primer" },
    warYears: [
      [1356, 1369],
      [1462, 1472],
    ],
    warNames: {
      1356: { en: "the War of the Two Peres against Castile", ca: "la Guerra dels Dos Peres contra Castella" },
      1462: { en: "the Catalan civil war", ca: "la guerra civil catalana" },
    },
    revolt: {
      year: 1462,
      name: { en: "the remença uprisings", ca: "les revoltes remences" },
      desc: {
        en: "when the bound peasants of Old Catalonia rose against the mals usos, demanding freedom from their lords",
        ca: "quan els pagesos de remença de la Catalunya Vella es van revoltar contra els mals usos, exigint la llibertat dels seus senyors",
      },
    },
    pilgrim: [
      { en: "Santa Maria de Montserrat", ca: "Santa Maria de Montserrat" },
      { en: "Sant Jaume de Galícia — Santiago de Compostela", ca: "Sant Jaume de Galícia — Santiago de Compostel·la" },
      { en: "the shrine of Sant Narcís at Girona", ca: "el santuari de Sant Narcís a Girona" },
    ],
    currency: "sous",
    landUnit: { en: "quarteres", ca: "quarteres" },
    // The Catalan hereu system: strict impartible succession, famously
    // rigid — a single heir took the mas whole, and the rest inherited
    // only a dowry or a cash legacy (the legítima).
    inheritance: "impartible",
  },
  italy: {
    name: { en: "Tuscany", ca: "la Toscana" },
    places: [
      { en: "the village of Colle Alberti, in the contado of Florence", ca: "el poble de Colle Alberti, al comtat de Florència" },
      { en: "a parish in the Mugello", ca: "una parròquia del Mugello" },
      { en: "the village of San Donnino, near Prato", ca: "el poble de San Donnino, prop de Prato" },
      { en: "the vill of Montefollonico, in the Sienese", ca: "la vila de Montefollonico, al territori de Siena" },
    ],
    maleNames: [
      "Giovanni",
      "Antonio",
      "Francesco",
      "Piero",
      "Niccolò",
      "Bartolomeo",
      "Domenico",
      "Matteo",
      "Andrea",
      "Lorenzo",
      "Jacopo",
      "Cristofano",
      "Nofri",
    ],
    femaleNames: ["Caterina", "Margherita", "Antonia", "Francesca", "Lucia", "Giovanna", "Piera", "Lisabetta", "Nanna", "Tessa", "Ginevra", "Bartolomea"],
    surnames: ["di Nardo", "del Rosso", "Fabbri", "di Lapo", "Vannucci", "del Bene", "di Cecco", "Mazzei", "Bonaccorsi", "di Vanni", "Ciardi"],
    marriageF: [15, 18],
    marriageM: [26, 32],
    famine: [1328, 1330],
    famineName: { en: "the dearth of 1328–30", ca: "la carestia de 1328–30" },
    warYears: [
      [1375, 1378],
      [1390, 1402],
      [1424, 1433],
    ],
    warNames: {
      1375: { en: "the War of the Eight Saints against the Pope", ca: "la Guerra dels Vuit Sants contra el Papa" },
      1390: { en: "the wars against Giangaleazzo of Milan", ca: "les guerres contra Gian Galeazzo de Milà" },
      1424: { en: "the wars against Milan and Lucca", ca: "les guerres contra Milà i Lucca" },
    },
    revolt: {
      year: 1378,
      name: { en: "the Revolt of the Ciompi", ca: "la Revolta dels Ciompi" },
      desc: {
        en: "when the wool-carders of Florence seized the government and demanded their own guild",
        ca: "quan els pentinadors de llana de Florència van prendre el govern i van exigir un gremi propi",
      },
    },
    pilgrim: [
      { en: "Rome for the Jubilee", ca: "Roma pel Jubileu" },
      { en: "Assisi and the tomb of St Francis", ca: "Assís i la tomba de Sant Francesc" },
      { en: "Our Lady of Impruneta", ca: "Nostra Senyora d'Impruneta" },
    ],
    currency: "soldi",
    landUnit: { en: "staiora", ca: "staiora" },
    // Tuscan customary practice widely favoured partible succession among
    // sons (often held for a time as an undivided fraterna before actually
    // splitting) — daughters received a dowry instead of a land share.
    inheritance: "partible",
  },
  germany: {
    name: { en: "the Holy Roman Empire", ca: "el Sacre Imperi Romanogermànic" },
    places: [
      { en: "the village of Obergrund, Franconia", ca: "el poble d'Obergrund, Francònia" },
      { en: "a parish in the Rhineland near Cologne", ca: "una parròquia de Renània, prop de Colònia" },
      { en: "the village of Lindheim, in the Wetterau", ca: "el poble de Lindheim, a la Wetterau" },
      { en: "the vill of Eschenbach, Swabia", ca: "la vila d'Eschenbach, Suàbia" },
    ],
    maleNames: ["Hans", "Heinrich", "Konrad", "Ulrich", "Friedrich", "Peter", "Nikolaus", "Michel", "Jörg", "Wilhelm", "Albrecht", "Wolf", "Endres"],
    femaleNames: ["Katharina", "Margarethe", "Anna", "Elisabeth", "Barbara", "Agnes", "Ursula", "Gertrud", "Adelheid", "Kunigunde", "Else"],
    surnames: ["Müller", "Weber", "Schmidt", "Wagner", "Fischer", "Zimmermann", "Schneider", "Bauer", "Koch", "Gerber", "Pfeifer", "Hofmann"],
    marriageF: [19, 24],
    marriageM: [24, 29],
    famine: [1315, 1317],
    famineName: { en: "the Great Famine", ca: "la Gran Fam" },
    warYears: [
      [1419, 1436],
      [1449, 1450],
    ],
    warNames: {
      1419: { en: "the Hussite wars in Bohemia", ca: "les guerres hussites a Bohèmia" },
      1449: { en: "the towns' war against the margrave", ca: "la guerra de les ciutats contra el marcgravi" },
    },
    revolt: null,
    pilgrim: [
      { en: "the Holy Blood of Wilsnack", ca: "la Santa Sang de Wilsnack" },
      { en: "the Three Kings at Cologne", ca: "els Tres Reis a Colònia" },
      { en: "Rome across the Alps for the Jubilee", ca: "Roma, travessant els Alps, pel Jubileu" },
    ],
    currency: "pfennige",
    landUnit: { en: "morgen", ca: "morgen" },
    inheritance: "impartible",
  },
  castile: {
    name: { en: "the Crown of Castile", ca: "la Corona de Castella" },
    places: [
      { en: "the village of Fresno el Viejo, in Old Castile", ca: "el poble de Fresno el Viejo, a la Vella Castella" },
      { en: "a hamlet in the Tierra de Campos", ca: "un llogaret de la Tierra de Campos" },
      { en: "the vill of Torrelobatón, near Valladolid", ca: "la vila de Torrelobatón, prop de Valladolid" },
      { en: "the village of Almazán, in Soria", ca: "el poble d'Almazán, a Sòria" },
    ],
    maleNames: [
      "Juan",
      "Pedro",
      "Alfonso",
      "Fernando",
      "Diego",
      "García",
      "Gonzalo",
      "Rodrigo",
      "Sancho",
      "Álvaro",
      "Martín",
      "Gómez",
      "Ruy",
      "Lope",
      "Suero",
      "Ordoño",
    ],
    femaleNames: ["María", "Catalina", "Elvira", "Teresa", "Urraca", "Sancha", "Leonor", "Beatriz", "Constanza", "Inés", "Mencía", "Aldonza"],
    surnames: ["García", "Fernández", "Rodríguez", "Martínez", "Sánchez", "Gómez", "López", "Díaz", "Ruiz", "Álvarez", "Ibáñez", "Ortiz"],
    marriageF: [16, 20],
    marriageM: [22, 27],
    // § the harvest: the Great Famine was a north-European rain event that
    // never reached Iberia. Castile and Portugal had their own subsistence
    // crisis at the turn of the century instead, which is what the
    // chroniclers there record — and what data/harvest.ts starves them in.
    famine: [1301, 1303],
    famineName: { en: "the hunger of the first years of the century", ca: "la fam dels primers anys del segle" },
    warYears: [
      [1366, 1369],
      [1475, 1479],
      [1482, 1492],
    ],
    warNames: {
      1366: { en: "the war between King Peter and his brother Henry of Trastámara", ca: "la guerra entre el rei Pere i el seu germà Enric de Trastàmara" },
      1475: { en: "the war of the Castilian succession", ca: "la guerra de successió castellana" },
      1482: { en: "the war against Granada", ca: "la guerra contra Granada" },
    },
    revolt: null,
    pilgrim: [
      { en: "Santiago de Compostela", ca: "Sant Jaume de Galícia — Santiago de Compostel·la" },
      { en: "Our Lady of Guadalupe", ca: "Nostra Senyora de Guadalupe" },
      { en: "the shrine of San Isidoro at León", ca: "el santuari de Sant Isidor a Lleó" },
    ],
    currency: "maravedís",
    landUnit: { en: "fanegas", ca: "fanegues" },
    // Castilian customary law (the Fuero Real, later the Siete Partidas)
    // reserved a legítima to every child rather than passing a holding whole
    // to one heir — closer to French/Tuscan partible custom than to the
    // English copyhold or Catalan hereu.
    inheritance: "partible",
  },
  scotland: {
    name: { en: "Scotland", ca: "Escòcia" },
    places: [
      { en: "the kirktoun of Kirkhaugh, Roxburghshire", ca: "el poble parroquial de Kirkhaugh, Roxburghshire" },
      { en: "the barony of Dalrigg, Fife", ca: "la baronia de Dalrigg, Fife" },
      { en: "the village of Auchinlea, Ayrshire", ca: "el poble d'Auchinlea, Ayrshire" },
      { en: "the fishertoun of Pittenhinny, near Dundee", ca: "el poble de pescadors de Pittenhinny, prop de Dundee" },
    ],
    maleNames: [
      "John",
      "William",
      "Robert",
      "Thomas",
      "David",
      "Andrew",
      "Duncan",
      "Malcolm",
      "Walter",
      "Alexander",
      "Patrick",
      "Adam",
      "Gilbert",
      "Fergus",
      "Simon",
      "Kenneth",
    ],
    femaleNames: [
      "Margaret",
      "Isobel",
      "Marion",
      "Christian",
      "Euphemia",
      "Agnes",
      "Janet",
      "Mariota",
      "Elizabeth",
      "Muriel",
      "Bethia",
      "Katherine",
      "Beatrix",
      "Elspeth",
    ],
    surnames: [
      "Broun",
      "Wricht",
      "Baxter",
      "Souter",
      "Tailyour",
      "Webster",
      "Walker",
      "Mylne",
      "Smyth",
      "Fletcher",
      "Cordiner",
      "Barbour",
      "Elliot",
      "Ker",
      "Cunningham",
      "Gray",
    ],
    marriageF: [18, 23],
    marriageM: [22, 27],
    famine: [1315, 1317],
    famineName: { en: "the Great Famine", ca: "la Gran Fam" },
    warYears: [
      [1296, 1328],
      [1332, 1357],
      [1480, 1482],
      [1496, 1497],
    ],
    warNames: {
      1296: { en: "the Scottish Wars of Independence", ca: "les Guerres d'Independència escoceses" },
      1332: { en: "the second war, over the Balliol claim", ca: "la segona guerra, per la pretensió de Balliol" },
      1480: { en: "the war over Berwick", ca: "la guerra per Berwick" },
      1496: { en: "King James's war for the pretender in England", ca: "la guerra del rei Jaume pel pretendent d'Anglaterra" },
    },
    revolt: null,
    pilgrim: [
      { en: "the shrine of St Andrew at St Andrews", ca: "el santuari de Sant Andreu a St Andrews" },
      { en: "St Ninian's shrine at Whithorn", ca: "el santuari de Sant Ninià a Whithorn" },
      { en: "the shrine of St Margaret at Dunfermline", ca: "el santuari de Santa Margarida a Dunfermline" },
    ],
    currency: "merks",
    landUnit: { en: "oxgangs", ca: "oxgangs" },
    // Lowland Scottish tenure (husbandlands, feudal barony holdings) followed
    // male primogeniture like English copyhold — a single heir took the
    // tenement whole, unlike the partible custom of France/Tuscany/Castile.
    inheritance: "impartible",
  },
  portugal: {
    name: { en: "Portugal", ca: "Portugal" },
    places: [
      { en: "the village of Vilarinho, in the Minho", ca: "el poble de Vilarinho, al Minho" },
      { en: "the hamlet of Fráguas, in the Beira, near Coimbra", ca: "el llogaret de Fráguas, a la Beira, prop de Coimbra" },
      { en: "the vill of Alvorge, in the Estremadura", ca: "la vila d'Alvorge, a l'Estremadura" },
      { en: "the village of Montouro, in the Alentejo", ca: "el poble de Montouro, a l'Alentejo" },
    ],
    maleNames: [
      "João",
      "Pedro",
      "Afonso",
      "Fernando",
      "Diogo",
      "Gil",
      "Martim",
      "Álvaro",
      "Vasco",
      "Gonçalo",
      "Rui",
      "Duarte",
      "Nuno",
      "Estêvão",
      "Lourenço",
      "Domingos",
    ],
    femaleNames: ["Maria", "Catarina", "Beatriz", "Leonor", "Isabel", "Branca", "Inês", "Constança", "Violante", "Guiomar", "Mécia", "Teresa"],
    surnames: [
      "Fernandes",
      "Rodrigues",
      "Gonçalves",
      "Álvares",
      "Pires",
      "Martins",
      "Vaz",
      "Lopes",
      "Dias",
      "Peixoto",
      "Ferreiro",
      "Carneiro",
      "Correia",
      "Esteves",
      "Marques",
      "Bravo",
    ],
    marriageF: [16, 20],
    marriageM: [21, 26],
    // § the harvest: the Great Famine was a north-European rain event that
    // never reached Iberia. Castile and Portugal had their own subsistence
    // crisis at the turn of the century instead, which is what the
    // chroniclers there record — and what data/harvest.ts starves them in.
    famine: [1301, 1303],
    famineName: { en: "the hunger of the first years of the century", ca: "la fam dels primers anys del segle" },
    warYears: [
      [1369, 1382],
      [1383, 1385],
      [1415, 1437],
      [1475, 1479],
    ],
    warNames: {
      1369: { en: "the Fernandine Wars with Castile", ca: "les Guerres Fernandines amb Castella" },
      1383: { en: "the 1383–85 crisis and the war for the crown", ca: "la crisi de 1383–85 i la guerra per la corona" },
      1415: { en: "the wars in Morocco", ca: "les guerres al Marroc" },
      1475: { en: "the war of the Castilian succession", ca: "la guerra de successió castellana" },
    },
    revolt: null,
    pilgrim: [
      { en: "Santiago de Compostela", ca: "Sant Jaume de Galícia — Santiago de Compostel·la" },
      { en: "Our Lady of Nazaré, on the coast", ca: "Nostra Senyora de Nazaré, a la costa" },
      { en: "the shrine of St Vincent at Lisbon cathedral", ca: "el santuari de Sant Vicenç a la catedral de Lisboa" },
    ],
    currency: "reais",
    landUnit: { en: "jeiras", ca: "jeiras" },
    // Portuguese customary law, like Castile's, reserved a legítima to every
    // child rather than passing a holding whole to one heir.
    inheritance: "partible",
  },
} satisfies Record<string, Region>;

/** Every supported region key — the single source of truth every other
 * region-keyed table is checked against at compile time (see the note above). */
export type RegionKey = keyof typeof REGION_DATA;

export const REGIONS: Record<string, Region> = REGION_DATA;
